import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Validators, FormBuilder } from '@angular/forms';
import { ProfileService } from 'src/app/api/profile.service';
import { User } from 'src/app/models/user';
import { SupportService } from 'src/app/api/support.service';
import { SupportCategory, SupportInput } from 'src/app/models/support';
import { timestampFromDate } from 'src/app/helpers/helpers';

@Component({
  selector: 'support-form',
  templateUrl: './support-form.component.html',
  styleUrls: ['./support-form.component.scss']
})
export class SupportFormComponent implements OnInit, OnChanges {
  @Input() type: string;

  loading = false;
  error: string = null;
  success = false;

  form = this.fb.group({
    main: this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      category: [null, Validators.required]
    }),
    general: this.fb.group({
      title: ['', Validators.required],
      message: ['', Validators.required]
    }),
    onlineMoneyTransfer: this.fb.group({
      deposit: [null, Validators.required],
      bankAccountHolderName: ['', Validators.required],
      transferDate: ['', Validators.required],
      // paymentReceipt: [null, Validators.required],
      passport: [null, Validators.required],
      message: ['']
    }),
    citizenshipResidencyValidation: this.fb.group({
      passport: [null, Validators.required],
      message: ['']
    }),
    phoneChange: this.fb.group({
      oldPhone: ['', Validators.required],
      newPhone: ['', Validators.required],
      passport: [null, Validators.required],
      personalPhoto: [null, Validators.required],
      message: ['']
    }),
    bankDetailsChange: this.fb.group({
      oldIban: [''],
      oldBankName: [''],
      oldBankAccountHolderName: [''],
      newIban: ['', Validators.required],
      newBankName: ['', Validators.required],
      newBankAccountHolderName: ['', Validators.required],
      newBranchName: ['', Validators.required],
      newBankAddress: ['', Validators.required],
      passport: [null, Validators.required],
      message: ['']
    }),
    otc: this.fb.group({
      name: ['', Validators.required],
      phone: ['', Validators.required],
      message: ['']
    }),
  });

  categories: SupportCategory[];

  user: User;

  constructor(
    private fb: FormBuilder,
    private profileSerivce: ProfileService,
    private supportService: SupportService
  ) { }

  ngOnInit() {
    this.profileSerivce.get().subscribe(response => {
      this.user = response;

      this.email.setValue(this.user.email);
    }, err => { });

    this.category.valueChanges.subscribe(() => {
      this.resetForm();
      this.error = null;
      this.success = false;
    });

    this.getCategories();
  }

  ngOnChanges(changes: SimpleChanges) {
    const typeChange = changes['type'];
    if (typeChange.currentValue !== typeChange.previousValue) {
      // TODO: check if the passed type is allowed
      // if (this.categories.includes(typeChange.currentValue)) {
        this.category.setValue(typeChange.currentValue);
      // }
    }
  }

  getCategories() {
    this.supportService.getCategories().subscribe(response => {
      this.categories = response;
    });
  }

  resetForm() {
    this.form.get('general').reset();
    this.form.get('onlineMoneyTransfer').reset();
    this.form.get('phoneChange').reset();
    this.form.get('bankDetailsChange').reset();
    this.form.get('otc').reset();
  }

  onSubmit() {
    this.success = false;
    this.error = null;

    if (this.isValid()) {
      let payload: SupportInput;

      switch (this.category.value) {
        case 'payment_request':
          payload = this.getOnlineMoneyTransferlPayload();
          break;
        case 'phoneChange':
          payload = this.getPhoneChangePayload();
          break;
        case 'bankDetailsChange':
          payload = this.getBankDetailsChangePayload();
          break;
        case 'citizenshipResidencyValidation':
          payload = this.getCitizenshipResidencyValidationPayload();
          break;
        case 'otc':
          payload = this.getOtcPayload();
          break;
        default:
          payload = this.getGeneralPayload();
      }

      this.sendTicket(payload);
    } else {
      this.error = 'Please fill out the required fields';
      this.scrollToTop();
    }
  }

  isValid() {
    if (this.mainGroup.invalid) {
      return false;
    }

    switch (this.category.value) {
      case 'payment_request':
        return this.onlineMoneyTransferGroup.valid;
      case 'phoneChange':
        return this.phoneChangeGroup.valid;
      case 'bankDetailsChange':
        return this.bankDetailsChangeGroup.valid;
      case 'citizenshipResidencyValidation':
        return this.citizenshipResidencyValidationGroup.valid;
      case 'otc':
        return this.otcGroup.valid;
      default:
        return this.generalGroup.valid;
    }
  }

  getGeneralPayload(): SupportInput {
    const formGroup = this.generalGroup;
    return {
      title: formGroup.get('title').value,
      message: formGroup.get('message').value
    };
  }

  getOnlineMoneyTransferlPayload(): SupportInput {
    const formGroup = this.onlineMoneyTransferGroup;
    const dateField = formGroup.get('transferDate');
    return {
      depositCode: formGroup.get('deposit').value ? formGroup.get('deposit').value.id : null,
      bankAccountHolderName: formGroup.get('bankAccountHolderName').value,
      transferDate: dateField.value ? timestampFromDate(dateField.value) : null,
      passport: formGroup.get('passport').value,
      message: formGroup.get('message').value
    };
  }

  getCitizenshipResidencyValidationPayload(): SupportInput {
    const formGroup = this.citizenshipResidencyValidationGroup;
    return {
      passport: formGroup.get('passport').value,
      message: formGroup.get('message').value
    };
  }

  getPhoneChangePayload(): SupportInput {
    const formGroup = this.phoneChangeGroup;
    return {
      oldPhone: formGroup.get('oldPhone').value,
      newPhone: formGroup.get('newPhone').value,
      passport: formGroup.get('passport').value,
      personalPhoto: formGroup.get('personalPhoto').value,
      message: formGroup.get('message').value
    };
  }

  getBankDetailsChangePayload(): SupportInput {
    const formGroup = this.bankDetailsChangeGroup;
    return {
      oldIban: formGroup.get('oldIban').value,
      oldBankName: formGroup.get('oldBankName').value,
      oldBankAccountHolderName: formGroup.get('oldBankAccountHolderName').value,
      newIban: formGroup.get('newIban').value,
      newBankName: formGroup.get('newBankName').value,
      newBankAccountHolderName: formGroup.get('newBankAccountHolderName').value,
      newBranchName: formGroup.get('newBranchName').value,
      newBankAddress: formGroup.get('newBankAddress').value,
      message: formGroup.get('message').value,
      passport: formGroup.get('passport').value
    };
  }

  getOtcPayload(): SupportInput {
    const formGroup = this.otcGroup;
    return {
      name: formGroup.get('name').value,
      phone: formGroup.get('phone').value,
      message: formGroup.get('message').value
    };
  }

  sendTicket(payload: SupportInput) {
    this.loading = true;

    payload.email = this.email.value;
    this.supportService.send(this.category.value, payload).subscribe(response => {
      this.loading = false;

      if (response) {
        this.category.setValue(null);
        this.success = true;
      } else {
        this.error = 'Something went wrong, please try again later';
      }

      this.scrollToTop();
    }, err => {
      this.loading = false;
      this.error = err.errors[0].message;
      this.scrollToTop();
    });
  }

  scrollToTop() {
    window.scroll(0, 0);
  }

  get mainGroup() { return this.form.get('main'); }
  get generalGroup() { return this.form.get('general'); }
  get onlineMoneyTransferGroup() { return this.form.get('onlineMoneyTransfer'); }
  get phoneChangeGroup() { return this.form.get('phoneChange'); }
  get bankDetailsChangeGroup() { return this.form.get('bankDetailsChange'); }
  get citizenshipResidencyValidationGroup() { return this.form.get('citizenshipResidencyValidation'); }
  get otcGroup() { return this.form.get('otc'); }

  get email() { return this.form.get('main.email'); }
  get category() { return this.form.get('main.category'); }
}
